import { useEffect } from "react"
import { Bar, BarChart, CartesianGrid, Cell, LabelList, XAxis, YAxis } from "recharts"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@components/common/card"
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@components/common/chart"
import useTranslator from "@hooks/use-translator"
import { cn, getDrillDownSubtitle, getDrillDownTitle } from "@pma/utils/helpers"
import {
  BookOpen,
  Calendar,
  Clock,
  Users,
  RefreshCw,
  CheckCircle2,
  XCircle,
  ClipboardList,
} from "lucide-react"
import { useAuth } from "@pma/stores/auth-store"
import { KpiCard, ChartCard, DrillDownSheet, DrillDownTable } from "../widgets"
import { useDrillDown } from "@hooks/use-drill-down"
import { ApprovalCard } from "./approval-card"
import { CommonAnalyticsProps } from "@pma/types"
import { useDiaryQueries } from "@pma/hooks/use-diary-queries"
import { useDiaryComputed } from "@pma/hooks/use-diary-computed"

export function WorkDiaryAnalytics({ filters, onCountLoaded }: CommonAnalyticsProps) {
  const { translate, locale } = useTranslator()
  const isHebrew = locale === "he"
  const { authConfig } = useAuth()
  const orgId = authConfig?.last_organization_id

  const { projects, allDiaryData, isLoading, refetchProjects } = useDiaryQueries(orgId, filters)

  const { state: drillDown, open: openDrillDown, close: closeDrillDown } = useDrillDown()

  const {
    totalDiaries,
    diariesByProject,
    approvalBreakdown,
    recentDiariesCount,
    recentDiaries,
    allFilteredDiaries,
  } = useDiaryComputed({ allDiaryData, filters })

  // Report count to parent for tab badge
  useEffect(() => {
    if (onCountLoaded && totalDiaries > 0) onCountLoaded(totalDiaries)
  }, [totalDiaries, onCountLoaded])

  // Loading state
  if (isLoading)
    return (
      <div className='flex flex-col gap-6' dir={isHebrew ? "rtl" : "ltr"}>
        <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4'>
          {[...Array(4)].map((_, i) => (
            <Card className='animate-pulse overflow-hidden border-0 shadow-md'>
              <CardContent className='flex items-center gap-4 p-5'>
                <div className='flex h-12 w-12 shrink-0 rounded-xl bg-gray-200' />
                <div className='flex-1 space-y-2'>
                  <div className='h-3 w-20 rounded bg-gray-200' />
                  <div className='h-7 w-14 rounded bg-gray-200' />
                </div>
                <div className='h-6 w-10 rounded-full bg-gray-200' />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card className='animate-pulse overflow-hidden shadow-md'>
          <CardHeader className='pb-2'>
            <div className='h-4 w-40 rounded bg-gray-200' />
          </CardHeader>
          <CardContent className='pt-0'>
            <div className='h-[280px] w-full rounded-lg bg-gray-100' />
          </CardContent>
        </Card>
        <div className='grid grid-cols-1 gap-4 sm:grid-cols-3'>
          {[...Array(3)].map((_, i) => (
            <Card className='animate-pulse overflow-hidden border-0 shadow-md'>
              <CardContent className='flex items-center gap-4 p-5'>
                <div className='flex h-12 w-12 shrink-0 rounded-xl bg-gray-200' />
                <div className='flex-1 space-y-2'>
                  <div className='h-3 w-20 rounded bg-gray-200' />
                  <div className='h-7 w-14 rounded bg-gray-200' />
                </div>
                <div className='h-6 w-10 rounded-full bg-gray-200' />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )

  if (!projects.length) {
    return (
      <div
        className='flex flex-col items-center justify-center py-24 text-center'
        dir={isHebrew ? "rtl" : "ltr"}
      >
        <div className='mb-5 flex h-20 w-20 items-center justify-center rounded-2xl bg-blue-50 shadow-inner'>
          <BookOpen className='h-10 w-10 text-blue-400' />
        </div>
        <h3 className='text-xl font-semibold text-gray-900'>{translate("No Work Diary Data")}</h3>
        <p className='mt-2 max-w-sm text-sm text-muted-foreground'>
          {translate("Create work diaries in your projects to see analytics here")}
        </p>
        <button
          onClick={() => refetchProjects()}
          className='mt-5 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow transition hover:bg-blue-700 active:scale-95'
        >
          <RefreshCw className='h-4 w-4' /> {translate("Retry")}
        </button>
      </div>
    )
  }

  // Chart config
  const projectConfig: ChartConfig = {
    count: { label: translate("Diaries"), color: "hsl(var(--chart-1))" },
  }
  diariesByProject.forEach((d) => {
    projectConfig[d.name] = { label: d.name, color: d.fill }
  })

  const totalApproval =
    approvalBreakdown.approved + approvalBreakdown.pending + approvalBreakdown.rejected

  // Helpers
  const getDiaryStatus = (diary: any): { label: string; className: string } => {
    const sa = diary?.supervisor_approval
    const ca = diary?.contractor_approval
    if (sa === true && ca === true)
      return { label: translate("Approved"), className: "bg-green-100 text-green-800" }
    if (sa === false || ca === false)
      return { label: translate("Rejected"), className: "bg-red-100 text-red-800" }
    return { label: translate("Pending"), className: "bg-amber-100 text-amber-800" }
  }

  const formatDate = (diary: any): string => {
    const raw = diary?.date || diary?.created_at
    if (!raw) return "—"
    const d = new Date(raw)
    if (isNaN(d.getTime())) return "—"
    return d.toLocaleDateString(isHebrew ? "he-IL" : "en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
  }

  return (
    <div className='flex flex-col gap-6 py-4' dir={isHebrew ? "rtl" : "ltr"}>
      {/* KPI Cards */}
      <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4'>
        <KpiCard
          label={translate("Total Diaries")}
          value={totalDiaries}
          icon={BookOpen}
          color='blue'
          gradient='from-blue-50 via-blue-50/60 to-white'
          iconBg='bg-blue-600'
          iconColor='text-white'
          valueColor='text-blue-800'
        />
        <KpiCard
          label={translate("This Week")}
          value={recentDiariesCount}
          icon={Calendar}
          color='green'
          gradient='from-emerald-50 via-emerald-50/60 to-white'
          iconBg='bg-emerald-600'
          iconColor='text-white'
          valueColor='text-emerald-800'
        />
        <KpiCard
          label={translate("Pending Approval")}
          value={approvalBreakdown.pending}
          icon={Clock}
          color='amber'
          gradient='from-amber-50 via-amber-50/60 to-white'
          iconBg='bg-amber-500'
          iconColor='text-white'
          valueColor='text-amber-800'
        />
        <KpiCard
          label={translate("Active Projects")}
          value={diariesByProject.length}
          icon={Users}
          color='purple'
          gradient='from-purple-50 via-purple-50/60 to-white'
          iconBg='bg-purple-600'
          iconColor='text-white'
          valueColor='text-purple-800'
        />
      </div>

      {/* Diaries per Project Bar Chart */}
      {diariesByProject.length > 0 && (
        <ChartCard
          title={translate("Diaries by Project")}
          description={translate("Work diaries per project")}
        >
          <ChartContainer config={projectConfig} className='h-[280px] w-full'>
            <BarChart data={diariesByProject} margin={{ top: 20, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} strokeDasharray='3 3' />
              <XAxis
                dataKey='name'
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                reversed={isHebrew}
                tickFormatter={(val) => (val.length > 12 ? val.slice(0, 12) + "…" : val)}
                className='text-xs'
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={10}
                orientation={isHebrew ? "right" : "left"}
                allowDecimals={false}
                className='text-xs'
              />
              <ChartTooltip cursor={false} content={<ChartTooltipContent />} />
              <Bar
                dataKey='count'
                radius={[6, 6, 0, 0]}
                barSize={36}
                className='cursor-pointer'
                onClick={(data: any) => {
                  const entry = data?.payload || data
                  if (entry?.projectId) {
                    openDrillDown({
                      type: "diary-project",
                      projectId: entry.projectId,
                      projectName: entry.name,
                    })
                  }
                }}
              >
                <LabelList
                  dataKey='count'
                  position='top'
                  className='fill-muted-foreground text-[11px] font-semibold'
                />
                {diariesByProject.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ChartContainer>
        </ChartCard>
      )}

      {/* Approval Summary Cards */}
      <div className='grid grid-cols-1 gap-4 sm:grid-cols-3'>
        <ApprovalCard
          label={translate("Approved")}
          count={approvalBreakdown.approved}
          total={totalApproval}
          icon={CheckCircle2}
          gradient='from-green-50 via-green-50/60 to-white'
          iconBg='bg-green-600'
          iconColor='text-white'
          numberColor='text-green-800'
          badgeClass='bg-green-100 text-green-800'
          onClick={() => openDrillDown({ type: "diary-approval", approvalState: "approved" })}
        />
        <ApprovalCard
          label={translate("Pending")}
          count={approvalBreakdown.pending}
          total={totalApproval}
          icon={Clock}
          gradient='from-amber-50 via-amber-50/60 to-white'
          iconBg='bg-amber-500'
          iconColor='text-white'
          numberColor='text-amber-800'
          badgeClass='bg-amber-100 text-amber-800'
          onClick={() => openDrillDown({ type: "diary-approval", approvalState: "pending" })}
        />
        <ApprovalCard
          label={translate("Rejected")}
          count={approvalBreakdown.rejected}
          total={totalApproval}
          icon={XCircle}
          gradient='from-red-50 via-red-50/60 to-white'
          iconBg='bg-red-600'
          iconColor='text-white'
          numberColor='text-red-800'
          badgeClass='bg-red-100 text-red-800'
          onClick={() => openDrillDown({ type: "diary-approval", approvalState: "rejected" })}
        />
      </div>

      {/* Drill-Down Sheet */}
      <DrillDownSheet
        open={!!drillDown}
        onClose={closeDrillDown}
        title={getDrillDownTitle(drillDown, translate)}
        subtitle={getDrillDownSubtitle(drillDown, translate)}
        width='wide'
      >
        {drillDown?.type === "diary-project" &&
          (() => {
            const rows = allFilteredDiaries
              .filter((d) => d.projectId === drillDown.projectId)
              .map((d) => ({
                date: formatDate(d.diary),
                _rawDate: new Date(d.diary?.date || d.diary?.created_at || 0).getTime(),
                status: getDiaryStatus(d.diary).label,
                statusClass: getDiaryStatus(d.diary).className,
                notes: d.diary?.notes || d.diary?.description || "—",
              }))
              .sort((a, b) => b._rawDate - a._rawDate)
            return (
              <DrillDownTable
                columns={[
                  { header: translate("Date"), accessor: "date", sortable: true },
                  {
                    header: translate("Status"),
                    accessor: "status",
                    sortable: true,
                    render: (v: string, row: any) => (
                      <span
                        className={cn(
                          "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold",
                          row.statusClass,
                        )}
                      >
                        {v}
                      </span>
                    ),
                  },
                  { header: translate("Notes"), accessor: "notes", sortable: false },
                ]}
                data={rows}
              />
            )
          })()}
        {drillDown?.type === "diary-approval" &&
          (() => {
            const classifyApproval = (diary: any): "approved" | "pending" | "rejected" => {
              const sa = diary?.supervisor_approval
              const ca = diary?.contractor_approval
              if (sa === true && ca === true) return "approved"
              if (sa === false || ca === false) return "rejected"
              return "pending"
            }
            const rows = allFilteredDiaries
              .filter((d) => classifyApproval(d.diary) === drillDown.approvalState)
              .map((d) => ({
                date: formatDate(d.diary),
                _rawDate: new Date(d.diary?.date || d.diary?.created_at || 0).getTime(),
                project: d.projectName,
                status: getDiaryStatus(d.diary).label,
                statusClass: getDiaryStatus(d.diary).className,
              }))
              .sort((a, b) => b._rawDate - a._rawDate)
            return (
              <DrillDownTable
                columns={[
                  { header: translate("Date"), accessor: "date", sortable: true },
                  { header: translate("Project"), accessor: "project", sortable: true },
                  {
                    header: translate("Status"),
                    accessor: "status",
                    sortable: true,
                    render: (v: string, row: any) => (
                      <span
                        className={cn(
                          "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold",
                          row.statusClass,
                        )}
                      >
                        {v}
                      </span>
                    ),
                  },
                ]}
                data={rows}
              />
            )
          })()}
      </DrillDownSheet>

      {/* Recent Diaries Mini-Table */}
      {recentDiaries.length > 0 && (
        <Card className='overflow-hidden shadow-md'>
          <CardHeader className='flex flex-row items-center gap-2 space-y-0 pb-2'>
            <ClipboardList className='h-4 w-4 text-muted-foreground' />
            <div>
              <CardTitle className='text-base font-semibold'>
                {translate("Recent Diaries")}
              </CardTitle>
              <CardDescription className='mt-0.5 text-xs'>
                {translate("Last 5 diaries created")}
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className='pt-0'>
            <div className='overflow-x-auto'>
              <table className='w-full text-sm'>
                <thead>
                  <tr className='border-b text-muted-foreground'>
                    <th
                      className={cn(
                        "py-2 text-xs font-medium uppercase tracking-wide",
                        isHebrew ? "pl-4 pr-0 text-right" : "pl-0 pr-4 text-left",
                      )}
                    >
                      {translate("Date")}
                    </th>
                    <th
                      className={cn(
                        "py-2 text-xs font-medium uppercase tracking-wide",
                        isHebrew ? "pl-4 pr-0 text-right" : "pl-0 pr-4 text-left",
                      )}
                    >
                      {translate("Project")}
                    </th>
                    <th className='py-2 text-right text-xs font-medium uppercase tracking-wide'>
                      {translate("Status")}
                    </th>
                  </tr>
                </thead>
                <tbody className='divide-y'>
                  {recentDiaries.map(({ diary, projectName }, idx) => {
                    const status = getDiaryStatus(diary)
                    return (
                      <tr key={idx} className='transition-colors hover:bg-muted/40'>
                        <td
                          className={cn(
                            "py-2.5 tabular-nums text-muted-foreground",
                            isHebrew ? "pl-4 pr-0" : "pl-0 pr-4",
                          )}
                        >
                          {formatDate(diary)}
                        </td>
                        <td
                          className={cn(
                            "max-w-[180px] truncate py-2.5 font-medium",
                            isHebrew ? "pl-4 pr-0" : "pl-0 pr-4",
                          )}
                        >
                          {projectName}
                        </td>
                        <td className='py-2.5 text-right'>
                          <span
                            className={cn(
                              "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold",
                              status.className,
                            )}
                          >
                            {status.label}
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export default WorkDiaryAnalytics
